var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ContactMethod } from "healthex-common/consentconfig.dtos";
import { computed, observable, runInAction } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { NamedRoutes } from "./NamedRoutes";
import { SimpleDialogModel } from "./SimpleDialogModel";
import { getObservableT } from "../Infra/i18next";
import { GFIDProvider } from "healthex-common/healthcareorganization.dtos";
const t = getObservableT("patients");
export class PatientProfileViewModel {
    constructor(container) {
        this.smsOptInDialogModel = new SimpleDialogModel();
        this.smsOptInConfirmDialogModel = new SimpleDialogModel();
        this.smsOptInPollApiTimerId = null;
        this.smsOptInDialogOpenLimitTimerId = null;
        this.preferredContactMethodLocal = null;
        this.optOutDialogOpen = false;
        this.removeFromCurrentResearch = false;
        this.selectedContactId = null;
        this.isSavingContactSelection = false;
        this.authManager = container.authManager;
        this.routingUIManager = container.routingUIManager;
        this.patientContactsUIManager = container.patientContactsUIManager;
        this.patientPreferencesManager = container.patientPreferencesManager;
        this.bannerUIManager = container.bannerUIManager;
    }
    run() { }
    stop() {
        this.clearSmsOptInPollApi();
        this.clearSmsOptInDialogOpenLimit();
    }
    get orgName() {
        return this.authManager.orgName;
    }
    get name() {
        return this.authManager.name;
    }
    get email() {
        return this.authManager.email;
    }
    get phone() {
        return this.authManager.formattedPhone;
    }
    get preferredContactMethodRemote() {
        return this.patientContactsUIManager.preferredContactMethod;
    }
    get isEmailRadioChecked() {
        return this.isRadioChecked(ContactMethod.EMAIL);
    }
    get isPhoneRadioChecked() {
        return this.isRadioChecked(ContactMethod.TEXT);
    }
    get isDoNotContactChecked() {
        return this.isRadioChecked(ContactMethod.DO_NOT_CONTACT);
    }
    get isOptedIn() {
        var _a, _b;
        if (this.patientPreferencesManager.preferences.value === NOT_LOADED)
            return NOT_LOADED;
        return !((_b = (_a = this.patientPreferencesManager.preferences.value) === null || _a === void 0 ? void 0 : _a.optedOut) !== null && _b !== void 0 ? _b : false);
    }
    get smsOptedOut() {
        var _a, _b;
        if (this.patientPreferencesManager.preferences.value === NOT_LOADED)
            return false;
        return (_b = (_a = this.patientPreferencesManager.preferences.value) === null || _a === void 0 ? void 0 : _a.smsOptedOut) !== null && _b !== void 0 ? _b : false;
    }
    get phoneNumberSuffix() {
        var _a, _b;
        if (this.patientPreferencesManager.preferences.value === NOT_LOADED)
            return null;
        return (_b = (_a = this.patientPreferencesManager.preferences.value) === null || _a === void 0 ? void 0 : _a.phoneNumberSuffix) !== null && _b !== void 0 ? _b : null;
    }
    get twilioFromPhoneNumber() {
        var _a, _b;
        if (this.patientPreferencesManager.preferences.value === NOT_LOADED)
            return null;
        return (_b = (_a = this.patientPreferencesManager.preferences.value) === null || _a === void 0 ? void 0 : _a.twilioFromPhoneNumber) !== null && _b !== void 0 ? _b : null;
    }
    get showPreferredContacts() {
        if (this.isOptedIn === NOT_LOADED)
            return NOT_LOADED;
        return this.isOptedIn && (!!this.email || !!this.phone);
    }
    get saveButtonEnabled() {
        return this.canSaveContactSelection;
    }
    get showSaveButton() {
        if (this.isOptedIn === NOT_LOADED)
            return false;
        return this.isOptedIn && this.showPreferredContacts;
    }
    get isClearLogin() {
        return this.authManager.gfidProvider === GFIDProvider.CLEAR;
    }
    get contactsLoading() {
        return this.patientPreferencesManager.preferencesWithContacts.value === NOT_LOADED;
    }
    get emails() {
        var _a;
        const prefs = this.patientPreferencesManager.preferencesWithContacts.value;
        if (prefs === NOT_LOADED || !prefs)
            return [];
        return (_a = prefs.emails) !== null && _a !== void 0 ? _a : [];
    }
    get phones() {
        var _a;
        const prefs = this.patientPreferencesManager.preferencesWithContacts.value;
        if (prefs === NOT_LOADED || !prefs)
            return [];
        return (_a = prefs.phones) !== null && _a !== void 0 ? _a : [];
    }
    get hasMultipleContacts() {
        return this.emails.length + this.phones.length > 1;
    }
    get currentSelectedContactId() {
        if (this.selectedContactId !== null) {
            return this.selectedContactId;
        }
        const primaryEmail = this.emails.find((e) => e.isPrimary);
        if (primaryEmail && this.preferredContactMethodRemote === ContactMethod.EMAIL) {
            return primaryEmail.id;
        }
        const primaryPhone = this.phones.find((p) => p.isPrimary);
        if (primaryPhone && this.preferredContactMethodRemote === ContactMethod.TEXT) {
            return primaryPhone.id;
        }
        if (primaryEmail)
            return primaryEmail.id;
        if (primaryPhone)
            return primaryPhone.id;
        return null;
    }
    selectContact(contactId) {
        this.selectedContactId = contactId;
    }
    isEmailSelected(emailId) {
        return this.currentSelectedContactId === emailId;
    }
    isPhoneSelected(phoneId) {
        return this.currentSelectedContactId === phoneId;
    }
    async saveContactSelection() {
        if (!this.selectedContactId)
            return;
        const selectedEmail = this.emails.find((e) => e.id === this.selectedContactId);
        const selectedPhone = this.phones.find((p) => p.id === this.selectedContactId);
        if (selectedPhone && this.smsOptedOut) {
            await this.showSmsOptInDialogForContactSelection(selectedPhone);
            return;
        }
        runInAction(() => {
            this.isSavingContactSelection = true;
        });
        try {
            if (selectedEmail) {
                await this.patientPreferencesManager.setPrimaryEmail(selectedEmail.id);
                await this.patientPreferencesManager.updatePatientPreferences({
                    preferredContactMethod: ContactMethod.EMAIL,
                });
                this.bannerUIManager.displayBanner({
                    text: t("Your contact method has been updated to {{email}}", { email: selectedEmail.email }),
                });
            }
            else if (selectedPhone) {
                await this.patientPreferencesManager.setPrimaryPhone(selectedPhone.id);
                await this.patientPreferencesManager.updatePatientPreferences({
                    preferredContactMethod: ContactMethod.TEXT,
                });
                this.bannerUIManager.displayBanner({
                    text: t("Your contact method has been updated to {{phone}}", { phone: selectedPhone.phoneNumber }),
                });
            }
            runInAction(() => {
                this.selectedContactId = null;
            });
        }
        catch (e) {
            console.error("Failed to save contact selection", e);
            this.bannerUIManager.displayBanner({
                text: t("Failed to update your contact method. Please try again"),
                type: "ERROR",
            });
        }
        finally {
            runInAction(() => {
                this.isSavingContactSelection = false;
            });
        }
    }
    async showSmsOptInDialogForContactSelection(selectedPhone) {
        this.smsOptInDialogModel.show();
        this.smsOptInPollApi();
        this.smsOptInDialogOpenLimit();
        const confirmed = await this.smsOptInDialogModel.showAndWait();
        if (this.smsOptInConfirmDialogModel.open)
            return;
        await this.smsOptInDialogClosedForContactSelection(confirmed, selectedPhone);
    }
    async smsOptInDialogClosedForContactSelection(isSuccess, selectedPhone) {
        this.clearSmsOptInPollApi();
        this.clearSmsOptInDialogOpenLimit();
        this.smsOptInDialogModel.open = false;
        if (isSuccess) {
            try {
                await this.patientPreferencesManager.setPrimaryPhone(selectedPhone.id);
                await this.patientPreferencesManager.updatePatientPreferences({
                    preferredContactMethod: ContactMethod.TEXT,
                });
                runInAction(() => {
                    this.selectedContactId = null;
                });
                this.bannerUIManager.displayBanner({
                    text: t("Your contact method has been updated to {{phone}}", { phone: selectedPhone.phoneNumber }),
                });
            }
            catch (e) {
                console.error("Failed to update preferred contact method", e);
                this.bannerUIManager.displayBanner({
                    text: t("Your contact method was unable to be updated to Phone. Please try again"),
                    type: "ERROR",
                });
            }
        }
        else {
            runInAction(() => {
                this.selectedContactId = null;
            });
            this.bannerUIManager.displayBanner({
                text: t("Your contact method was unable to be updated to Phone. Please try again"),
                type: "ERROR",
            });
        }
    }
    get canSaveContactSelection() {
        if (this.selectedContactId === null)
            return false;
        if (this.isSavingContactSelection)
            return false;
        const selectedEmail = this.emails.find((e) => e.id === this.selectedContactId);
        const selectedPhone = this.phones.find((p) => p.id === this.selectedContactId);
        if (selectedEmail) {
            return !selectedEmail.isPrimary || this.preferredContactMethodRemote !== ContactMethod.EMAIL;
        }
        if (selectedPhone) {
            return !selectedPhone.isPrimary || this.preferredContactMethodRemote !== ContactMethod.TEXT;
        }
        return false;
    }
    isRadioChecked(contactMethod) {
        if (this.preferredContactMethodLocal === null)
            return this.preferredContactMethodRemote === contactMethod;
        return this.preferredContactMethodLocal === contactMethod;
    }
    async updatedPreferredContactMethodLocal(contactMethod) {
        this.preferredContactMethodLocal = contactMethod;
        if (contactMethod === ContactMethod.TEXT && this.smsOptedOut) {
            await this.showSmsOptInDialog();
        }
    }
    updateRemoveFromCurrentResearch(val) {
        this.removeFromCurrentResearch = val;
    }
    goBack() {
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_HOME);
    }
    async save() {
        await this.saveContactSelection();
    }
    clearSmsOptInPollApi() {
        if (this.smsOptInPollApiTimerId !== null) {
            window.clearTimeout(this.smsOptInPollApiTimerId);
        }
    }
    calculateSmsOptInPollInterval(retries) {
        if (retries === 0)
            return 1000;
        if (retries === 1)
            return 10000;
        if (retries === 2)
            return 7500;
        if (retries < 10)
            return 5000;
        if (retries < 20)
            return 7500;
        if (retries < 30)
            return 10000;
        return 12000;
    }
    smsOptInPollApi(retries = 0) {
        this.clearSmsOptInPollApi();
        this.smsOptInPollApiTimerId = window.setTimeout(async () => {
            await this.patientPreferencesManager.preferences.waitForReload();
            if (!this.smsOptedOut) {
                this.smsOptInDialogModel.onChoice(true);
            }
            else {
                this.smsOptInPollApi(retries + 1);
            }
        }, this.calculateSmsOptInPollInterval(retries));
    }
    clearSmsOptInDialogOpenLimit() {
        if (this.smsOptInDialogOpenLimitTimerId !== null) {
            window.clearTimeout(this.smsOptInDialogOpenLimitTimerId);
        }
    }
    smsOptInDialogOpenLimit() {
        this.smsOptInDialogOpenLimitTimerId = window.setTimeout(async () => {
            this.smsOptInDialogModel.onChoice(false);
        }, 60000 * 15);
    }
    async showSmsOptInDialog() {
        this.smsOptInDialogModel.show();
        this.smsOptInPollApi();
        this.smsOptInDialogOpenLimit();
        const confirmed = await this.smsOptInDialogModel.showAndWait();
        if (this.smsOptInConfirmDialogModel.open)
            return;
        await this.smsOptInDialogClosed(confirmed);
    }
    async showSmsOptInConfirmDialog() {
        this.smsOptInConfirmDialogModel.show();
        if (this.smsOptInDialogModel.open)
            this.smsOptInDialogModel.onChoice(false);
        const confirmed = await this.smsOptInConfirmDialogModel.showAndWait();
        await this.smsOptInDialogClosed(confirmed);
    }
    async smsOptInDialogClosed(isSuccess) {
        this.clearSmsOptInPollApi();
        this.clearSmsOptInDialogOpenLimit();
        this.smsOptInDialogModel.open = false;
        if (isSuccess) {
            try {
                this.preferredContactMethodLocal = ContactMethod.TEXT;
                await this.patientPreferencesManager.updatePatientPreferences({
                    preferredContactMethod: ContactMethod.TEXT,
                });
                this.bannerUIManager.displayBanner({ text: t("Your contact method has been successfully updated to Phone") });
            }
            catch (e) {
                console.error("Failed to update preferred contact method", e);
                this.revertPreferredContactMethodToRemote();
                this.bannerUIManager.displayBanner({
                    text: t("Your contact method was unable to be updated to Phone. Please try again"),
                    type: "ERROR",
                });
            }
        }
        else {
            this.revertPreferredContactMethodToRemote();
            this.bannerUIManager.displayBanner({
                text: t("Your contact method was unable to be updated to Phone. Please try again"),
                type: "ERROR",
            });
        }
    }
    revertPreferredContactMethodToRemote() {
        if (this.preferredContactMethodRemote !== null &&
            this.preferredContactMethodRemote !== NOT_LOADED &&
            this.preferredContactMethodRemote !== ContactMethod.CALL) {
            this.preferredContactMethodLocal = this.preferredContactMethodRemote;
        }
    }
    async deleteMyAccount() {
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_DELETE_MY_ACCOUNT);
    }
}
__decorate([
    observable
], PatientProfileViewModel.prototype, "preferredContactMethodLocal", void 0);
__decorate([
    observable
], PatientProfileViewModel.prototype, "optOutDialogOpen", void 0);
__decorate([
    observable
], PatientProfileViewModel.prototype, "removeFromCurrentResearch", void 0);
__decorate([
    observable
], PatientProfileViewModel.prototype, "selectedContactId", void 0);
__decorate([
    observable
], PatientProfileViewModel.prototype, "isSavingContactSelection", void 0);
__decorate([
    computed
], PatientProfileViewModel.prototype, "orgName", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "name", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "email", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "phone", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "preferredContactMethodRemote", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "isEmailRadioChecked", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "isPhoneRadioChecked", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "isDoNotContactChecked", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "isOptedIn", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "smsOptedOut", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "phoneNumberSuffix", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "twilioFromPhoneNumber", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "showPreferredContacts", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "saveButtonEnabled", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "showSaveButton", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "isClearLogin", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "contactsLoading", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "emails", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "phones", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "hasMultipleContacts", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "currentSelectedContactId", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "canSaveContactSelection", null);
